import { useState, useEffect } from 'react';
import { Newspaper, AlertTriangle } from 'lucide-react';
import ModuleContainer from '../layout/ModuleContainer.jsx';

const severityColors = {
  1: 'text-korinth-muted',
  2: 'text-korinth-text',
  3: 'text-korinth-warning',
  4: 'text-orange-400',
  5: 'text-korinth-danger',
};

function NewsItem({ headline, source, severity, category, assets, direction, publishedAt }) {
  const color = severityColors[severity] || 'text-korinth-muted';

  return (
    <div className="flex gap-3 py-3 border-b border-korinth-border/50 last:border-0">
      <div className="flex-shrink-0 mt-0.5">
        {severity >= 4 ? (
          <AlertTriangle className={`w-4 h-4 ${color}`} />
        ) : (
          <Newspaper className="w-4 h-4 text-korinth-muted" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className={`text-sm ${color}`}>{headline}</p>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <span className="text-[10px] font-mono text-korinth-muted">{source}</span>
          {category && (
            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-korinth-border text-korinth-muted">
              {category}
            </span>
          )}
          {severity && (
            <span className={`text-[10px] font-mono font-bold ${color}`}>
              SEV-{severity}
            </span>
          )}
          {assets && assets.map((a) => (
            <span key={a} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-korinth-accent/10 text-korinth-accent">
              {a}
            </span>
          ))}
        </div>
      </div>
      <div className="flex-shrink-0 text-[10px] font-mono text-korinth-muted">
        {publishedAt ? new Date(publishedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Dubai' }) : ''}
      </div>
    </div>
  );
}

export default function NewsFeed() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchNews() {
      try {
        const res = await fetch('/api/news/latest');
        if (res.ok) {
          const data = await res.json();
          setNews(data);
        }
      } catch {
        // Backend not running yet
      } finally {
        setLoading(false);
      }
    }
    fetchNews();
    const interval = setInterval(fetchNews, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <ModuleContainer title="News Intelligence" subtitle="AI-classified headlines with severity, asset impact, and direction">
      {loading && news.length === 0 ? (
        <div className="text-center text-korinth-muted text-xs py-8 font-mono">
          Awaiting news feed connection...
        </div>
      ) : news.length === 0 ? (
        <div className="text-center text-korinth-muted text-xs py-8 font-mono">
          No classified headlines yet. Start the backend to begin ingesting news.
        </div>
      ) : (
        <div className="max-h-96 overflow-y-auto">
          {news.map((item, i) => (
            <NewsItem
              key={item.id || i}
              headline={item.headline}
              source={item.source}
              severity={item.severity}
              category={item.category}
              assets={item.assets_impacted ? JSON.parse(item.assets_impacted) : []}
              direction={item.direction}
              publishedAt={item.published_at}
            />
          ))}
        </div>
      )}
    </ModuleContainer>
  );
}
